export const serviceSlug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const SERVICE_DETAILS = [
  {
    slug: "enterprise-software",
    title: "Enterprise Software",
    tagline: "Systems that run the business — not slow it down.",
    meta: "Farmint builds scalable, secure enterprise software for complex business operations — engineered for reliability, integration and long-term maintainability.",
    overview:
      "Enterprise software earns its keep by handling real complexity: multiple departments, strict permissions, heavy integrations and zero tolerance for downtime. We design and build line-of-business systems that stay maintainable for years, not quarters.",
    deliver: [
      "Line-of-business platforms tailored to your operations",
      "Workflow, approval and escalation engines",
      "Role-based access control and complete audit trails",
      "Integration with existing ERP, CRM and internal systems",
      "Operational reporting and management dashboards",
      "Structured data migration from legacy tools",
    ],
    approach: [
      { title: "Domain discovery", desc: "We map how your business actually runs — people, processes, exceptions and systems — before writing a line of code." },
      { title: "Architecture & integration design", desc: "A system design that respects your existing landscape and leaves clean seams for future change." },
      { title: "Iterative delivery", desc: "Working software in thin, reviewable slices so stakeholders steer the product as it forms." },
      { title: "Hardening & rollout", desc: "Performance, security and migration rehearsal before phased production rollout." },
    ],
    idealFor: ["Enterprises modernizing operations", "Companies outgrowing spreadsheets", "Multi-department workflows", "Regulated environments"],
    stack: ["React", "Node.js / Python", "PostgreSQL", "Redis", "AWS / Azure"],
    related: ["custom-software-development", "web-application-development", "application-support-maintenance"],
  },
  {
    slug: "enterprise-saas-software",
    title: "Enterprise SaaS Software",
    tagline: "Multi-tenant platforms built to scale from day one.",
    meta: "Farmint designs and develops multi-tenant SaaS platforms engineered for scale, reliability and growth — tenancy, billing-readiness and APIs built in.",
    overview:
      "A real SaaS product is more than a web app with logins. Tenancy isolation, onboarding, metering, admin tooling and API-first design decide whether you scale smoothly or rebuild at the worst moment. We build SaaS foundations that don't need rescuing later.",
    deliver: [
      "Tenant isolation and data segregation architecture",
      "Subscription, plan and metering foundations",
      "Self-serve tenant onboarding and admin consoles",
      "API-first architecture for partners and integrations",
      "Usage analytics for product and revenue decisions",
      "White-label and multi-brand readiness",
    ],
    approach: [
      { title: "Product & tenancy model", desc: "We define tenants, plans, roles and data boundaries — the decisions everything else depends on." },
      { title: "Core platform build", desc: "The multi-tenant core, built API-first with the interfaces your roadmap implies." },
      { title: "Billing & onboarding", desc: "Metering, subscription logic and onboarding flows that let customers self-serve." },
      { title: "Scale & reliability", desc: "Observability, performance budgets and infrastructure that grows with your customer base." },
    ],
    idealFor: ["SaaS founders", "Product companies", "Internal tools going commercial", "B2B platforms"],
    stack: ["React", "FastAPI / Node.js", "PostgreSQL", "Usage metering", "AWS / GCP"],
    related: ["web-application-development", "aws-azure-gcp", "devops-cloud-ops"],
  },
  {
    slug: "mobile-app-development",
    title: "Mobile App Development",
    tagline: "Native-quality apps your users keep on their home screen.",
    meta: "Farmint builds native-quality Android and iOS applications for customers, employees and business operations — from MVP to app store launch.",
    overview:
      "Mobile users forgive nothing: slow screens, dead offline states and clumsy flows get uninstalled. We build Android and iOS apps with native-quality feel, disciplined offline behaviour and a backend engineered for mobile realities.",
    deliver: [
      "Native-quality iOS and Android applications",
      "Offline-first data sync and storage",
      "Push notifications, deep links and app shortcuts",
      "App Store and Play Store release management",
      "Analytics, crash monitoring and performance tracking",
      "Backend APIs designed for mobile clients",
    ],
    approach: [
      { title: "UX & flows", desc: "Screens, gestures and journeys designed for thumbs, interruptions and patchy networks." },
      { title: "Architecture", desc: "A shared core where it helps, platform-native polish where it matters." },
      { title: "Iterative builds", desc: "Installable builds from early on — you test the real app, not mockups." },
      { title: "Launch & support", desc: "Store submission, phased rollout, crash triage and version cadence." },
    ],
    idealFor: ["Customer-facing apps", "Field workforce apps", "Internal employee apps", "MVP to v1 products"],
    stack: ["React Native / Flutter", "Kotlin / Swift", "FastAPI", "Firebase", "Push & deep links"],
    related: ["web-application-development", "custom-software-development", "whatsapp-integration"],
  },
  {
    slug: "web-application-development",
    title: "Web Application Development",
    tagline: "Fast, resilient web apps with serious backends.",
    meta: "Farmint builds modern, responsive, high-performance web applications with robust backend architecture, realtime features and strong engineering practices.",
    overview:
      "A web application lives or dies on two things: how fast it feels and how well the backend holds up. We build responsive, high-performance frontends on top of APIs and data layers designed for correctness, scale and change.",
    deliver: [
      "Responsive, high-performance web applications",
      "Robust backend architecture and well-designed APIs",
      "Realtime features — live updates, collaboration, presence",
      "Authentication, roles and permission systems",
      "Performance budgets and Core Web Vitals tuning",
      "Automated testing and CI from the first sprint",
    ],
    approach: [
      { title: "Product definition", desc: "Flows, states and edge cases mapped before build — including the unhappy paths." },
      { title: "Foundation first", desc: "Data model, API contracts and auth designed so features compose instead of collide." },
      { title: "Iterative delivery", desc: "Deployable increments every cycle, reviewed against real usage scenarios." },
      { title: "Performance pass", desc: "Measured, profiled and tuned — speed is verified, not assumed." },
    ],
    idealFor: ["SaaS dashboards", "Customer portals", "Internal tools", "Marketplace products"],
    stack: ["React", "TypeScript", "FastAPI / Node.js", "PostgreSQL", "Redis"],
    related: ["enterprise-saas-software", "enterprise-software", "generative-ai"],
  },
  {
    slug: "whatsapp-integration",
    title: "WhatsApp Integration",
    tagline: "Meet your customers where they already are.",
    meta: "Farmint integrates WhatsApp into business workflows — notifications, customer engagement, chatbots and automation on the WhatsApp Business Platform.",
    overview:
      "For many businesses, WhatsApp is the highest-open-rate channel that exists. We integrate it properly into your operations — notifications, guided flows, support and automation — connected to your systems instead of living in someone's phone.",
    deliver: [
      "WhatsApp Business Platform (Cloud API) setup and onboarding",
      "Template messages, alerts and transactional notifications",
      "Chatbots and guided conversational flows",
      "Integration with your CRM, helpdesk and order systems",
      "Campaign and customer engagement automation",
      "Opt-in compliance, delivery analytics and reporting",
    ],
    approach: [
      { title: "Use-case mapping", desc: "We identify which conversations and notifications genuinely belong on WhatsApp." },
      { title: "API & compliance setup", desc: "Business verification, templates, opt-in flows and quality-rating hygiene." },
      { title: "Flows & automation", desc: "Bots, handoff to humans and system triggers wired into your stack." },
      { title: "Launch & optimization", desc: "Delivery metrics, template performance and iteration on real conversations." },
    ],
    idealFor: ["Customer support teams", "Order & booking updates", "Sales engagement", "Service businesses"],
    stack: ["WhatsApp Business Platform", "Cloud API & webhooks", "Node.js / Python", "CRM connectors"],
    related: ["agentic-ai", "custom-software-development", "mobile-app-development"],
  },
  {
    slug: "generative-ai",
    title: "Generative AI",
    tagline: "LLM features that survive contact with real users.",
    meta: "Farmint builds practical Generative AI applications — LLM-powered content generation, intelligent search, summarization and knowledge workflows with guardrails.",
    overview:
      "The demo is easy; the production system is not. We build Generative AI features with the unglamorous parts done properly — retrieval over your actual data, evaluation against real cases, guardrails, and cost that scales sanely.",
    deliver: [
      "LLM-powered product features and copilots",
      "Retrieval-augmented generation over your knowledge base",
      "Intelligent search, summarization and extraction",
      "Content generation and drafting workflows",
      "Evaluation suites, guardrails and output controls",
      "Cost, latency and quality optimization",
    ],
    approach: [
      { title: "Use-case & data audit", desc: "We verify the problem needs an LLM, and that your data can support it." },
      { title: "Pilot with a baseline", desc: "A working pilot measured against a clear quality and cost baseline." },
      { title: "Guardrails & evals", desc: "Automated evaluation sets and output controls before anything faces users." },
      { title: "Production rollout", desc: "Monitoring, fallback behaviour and continuous quality tracking in production." },
    ],
    idealFor: ["Knowledge-heavy teams", "Support & documentation", "Content operations", "Product copilots"],
    stack: ["OpenAI / Anthropic / Gemini", "Vector databases", "Python", "LLM orchestration"],
    related: ["agentic-ai", "web-application-development", "custom-software-development"],
  },
  {
    slug: "agentic-ai",
    title: "Agentic AI",
    tagline: "Agents that reason, act and report back.",
    meta: "Farmint designs AI agents that reason through tasks, use tools, interact with business systems and execute multi-step workflows — with oversight built in.",
    overview:
      "An agent is only useful when it can act: call tools, touch systems and complete multi-step work. We design agentic systems with explicit permissions, human checkpoints and full trace logs — autonomy you can audit, not a black box you hope behaves.",
    deliver: [
      "Agents that reason and plan multi-step tasks",
      "Tool and API use within explicit permission boundaries",
      "Human-in-the-loop approvals for sensitive actions",
      "Multi-agent workflows and delegation patterns",
      "Full observability — traces, decisions and action logs",
      "Safe rollout with fallbacks and kill switches",
    ],
    approach: [
      { title: "Workflow decomposition", desc: "We break the target process into steps an agent can own, verify or escalate." },
      { title: "Tools & permissions", desc: "Every tool the agent can call is designed, scoped and tested independently." },
      { title: "Supervised pilots", desc: "Agents run with human approval on real work before earning autonomy." },
      { title: "Autonomy with oversight", desc: "Graduated permissions, anomaly detection and complete audit trails." },
    ],
    idealFor: ["Operations automation", "Multi-system workflows", "Back-office processes", "AI-first products"],
    stack: ["LLM orchestration", "Function calling / tool APIs", "Python", "Queue-based workers"],
    related: ["generative-ai", "whatsapp-integration", "custom-software-development"],
  },
  {
    slug: "custom-software-development",
    title: "Custom Software Development",
    tagline: "Software shaped around your business — not the other way around.",
    meta: "Farmint builds custom software around your unique business requirements instead of forcing your business into generic products.",
    overview:
      "Generic tools force generic processes. When your workflow is your advantage, software should encode it — not fight it. We build bespoke systems around how your business actually works, with documentation and ownership that keep you in control.",
    deliver: [
      "Bespoke applications built around your workflows",
      "Your business rules encoded precisely — including edge cases",
      "Integration with anything that exposes an API",
      "Legacy modernization with staged, low-risk paths",
      "Complete documentation and clean handover",
      "Long-term evolution and support options",
    ],
    approach: [
      { title: "Deep discovery", desc: "We learn the process from the people who run it — including its exceptions and workarounds." },
      { title: "Thin-slice MVP", desc: "The smallest version that proves the core workflow end to end." },
      { title: "Iterative expansion", desc: "Capabilities added in priority order, validated by real users each cycle." },
      { title: "Ownership options", desc: "Full documentation, clean code and optional knowledge transfer to your team." },
    ],
    idealFor: ["Unique business processes", "Founders with novel ideas", "Teams stuck with generic tools", "Regulated niches"],
    stack: ["Chosen per problem", "React", "Python / Node.js", "PostgreSQL", "Cloud-native"],
    related: ["enterprise-software", "web-application-development", "application-support-maintenance"],
  },
  {
    slug: "devops-cloud-ops",
    title: "DevOps & Cloud Ops",
    tagline: "Releases so reliable they become boring.",
    meta: "Farmint provides DevOps and cloud operations — CI/CD, infrastructure automation, observability, deployment, reliability and cloud cost optimization.",
    overview:
      "Great engineering dies without great operations. We build the pipelines, infrastructure automation and observability that make deployments routine, incidents diagnosable and infrastructure bills explainable.",
    deliver: [
      "CI/CD pipelines with automated quality gates",
      "Infrastructure as Code — reproducible environments",
      "Containerization and orchestration",
      "Observability: logs, metrics, traces and alerting",
      "Cloud cost analysis and optimization",
      "Incident response practices and SLO definition",
    ],
    approach: [
      { title: "Pipeline & environment audit", desc: "We map your current path from commit to production and where it breaks." },
      { title: "Automation foundation", desc: "IaC, CI/CD and environments that any engineer can reproduce." },
      { title: "Observability buildout", desc: "Dashboards and alerts that answer 'what broke and why' in minutes." },
      { title: "Reliability practice", desc: "SLOs, runbooks and review rhythms that keep operations healthy long-term." },
    ],
    idealFor: ["Teams shipping weekly or faster", "Scaling products", "Regulated deployment needs", "Cost-heavy infrastructure"],
    stack: ["Docker", "Kubernetes", "Terraform", "GitHub Actions / GitLab CI", "Prometheus / Grafana"],
    related: ["aws-azure-gcp", "application-support-maintenance", "enterprise-saas-software"],
  },
  {
    slug: "aws-azure-gcp",
    title: "AWS / Azure / GCP",
    tagline: "Cloud architecture that earns its invoice.",
    meta: "Farmint designs, deploys and operates cloud-native applications across AWS, Microsoft Azure and Google Cloud Platform — architecture, migration and cost governance.",
    overview:
      "The cloud rewards good architecture and punishes improvisation — in outages or in invoices. We design, migrate and operate workloads across AWS, Azure and GCP with security, recovery and cost treated as first-class requirements.",
    deliver: [
      "Cloud architecture design for new and existing systems",
      "Migration planning and low-risk execution",
      "Serverless, container and managed platform setups",
      "Networking, security and IAM done properly",
      "Backup, disaster recovery and failover design",
      "Cost governance, budgets and optimization reviews",
    ],
    approach: [
      { title: "Workload assessment", desc: "What runs where, what it needs, and what it's actually costing you." },
      { title: "Target architecture", desc: "A cloud design matched to your scale — not a reference diagram off a blog." },
      { title: "Migration or build", desc: "Staged execution with rollback paths at every step." },
      { title: "Operate & optimize", desc: "Monitoring, security posture and cost reviews on an ongoing rhythm." },
    ],
    idealFor: ["Cloud migrations", "Multi-cloud strategy", "New cloud-native products", "Compliance-driven hosting"],
    stack: ["AWS", "Microsoft Azure", "Google Cloud", "Terraform", "Kubernetes"],
    related: ["devops-cloud-ops", "enterprise-saas-software", "application-support-maintenance"],
  },
  {
    slug: "application-support-maintenance",
    title: "Application Support & Maintenance",
    tagline: "Your production system, watched by people who can fix it.",
    meta: "Farmint provides ongoing application support and maintenance — monitoring, issue resolution, optimization, upgrades and technical maintenance for production applications.",
    overview:
      "Software doesn't end at launch — it starts. We take responsibility for production systems with real monitoring, disciplined incident response and a steady cadence of upgrades and improvements, so your application stays healthy as the world changes around it.",
    deliver: [
      "SLA-based monitoring and incident response",
      "Bug triage, fixes and hotfix releases",
      "Performance monitoring and optimization",
      "Dependency, framework and security upgrades",
      "Small feature enhancements and improvements",
      "Runbooks, knowledge base and system documentation",
    ],
    approach: [
      { title: "Audit & baseline", desc: "We learn the system, document it and establish its current health baseline." },
      { title: "Monitoring & alerting", desc: "Coverage for the failures that matter, with alerts routed to engineers." },
      { title: "Steady-state support", desc: "Tickets, incidents and fixes handled against agreed response targets." },
      { title: "Continuous improvement", desc: "Monthly health reviews, upgrade cadence and performance trendlines." },
    ],
    idealFor: ["Products without in-house teams", "Legacy apps needing care", "Post-launch SaaS", "Seasonal-traffic systems"],
    stack: ["Observability stack", "Ticketing & SLAs", "Your existing stack", "CI/CD"],
    related: ["devops-cloud-ops", "aws-azure-gcp", "custom-software-development"],
  },
];

export const getServiceBySlug = (slug) => SERVICE_DETAILS.find((s) => s.slug === slug);
